import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Plus,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { SaveBar } from '@/components/ui/save-bar';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { cn } from '@/lib/cn';

/**
 * /brief/menu — Zabiegi megamenu editor.
 *
 * Stores `brief.nav_override` (see packages/brief/rich.ts). Absent =
 * pipeline auto-derives from inline_services + procedures. When set,
 * this override fully drives the megamenu — owner picks which
 * procedures sit in which column + labels them + toggles right-rail
 * built-ins + adds custom quick-links.
 *
 * Intentionally light on UX polish (no drag-and-drop, no live preview)
 * — Phase 1 priority is "an editor that works" over "an editor that
 * feels great". Reorder is up/down buttons. Column reorder is move
 * left/right. Empty state shows an "Auto-fill from your procedures"
 * button that seeds the columns once.
 */

export const Route = createFileRoute('/_app/brief/menu')({
  staticData: { crumb: 'Menu nawigacji' },
  component: MenuPage,
});

interface NavOverrideItem {
  label: string;
  service_slug: string;
}
interface NavOverrideColumn {
  label: string;
  items: NavOverrideItem[];
}
interface NavOverrideRail {
  show_about: boolean;
  show_reviews: boolean;
  show_contact: boolean;
  custom: Array<{ label: string; href: string }>;
}
interface NavOverride {
  columns: NavOverrideColumn[];
  right_rail?: NavOverrideRail;
}

const VET_CATEGORY_LABEL_PL: Record<string, string> = {
  consultation: 'Konsultacje',
  preventive: 'Profilaktyka',
  surgery: 'Chirurgia',
  dental: 'Stomatologia',
  diagnostic: 'Diagnostyka',
  rehabilitation: 'Rehabilitacja',
  specialty: 'Specjalistyka',
  emergency: 'Intensywna opieka',
  grooming: 'Pielęgnacja',
  end_of_life: 'Pożegnanie',
  general: 'Pozostałe',
  konsultacja: 'Konsultacje',
  profilaktyka: 'Profilaktyka',
  chirurgia: 'Chirurgia',
  diagnostyka: 'Diagnostyka',
  stomatologia: 'Stomatologia',
  'stomatologia-zwierzat': 'Stomatologia',
  rehabilitacja: 'Rehabilitacja',
  fizjoterapia: 'Rehabilitacja',
  fizykoterapia: 'Rehabilitacja',
  eutanazja: 'Pożegnanie',
  'intensywna-opieka': 'Intensywna opieka',
  pielegnacja: 'Pielęgnacja',
  hospitalizacja: 'Intensywna opieka',
  szczepienia: 'Profilaktyka',
  'usg-rtg': 'Diagnostyka',
  laboratorium: 'Diagnostyka',
  kardiologia: 'Kardiologia',
  dermatologia: 'Dermatologia',
  okulistyka: 'Okulistyka',
  ortopedia: 'Ortopedia',
  onkologia: 'Onkologia',
  anestezjologia: 'Anestezjologia',
  neurologia: 'Neurologia',
  rozrod: 'Rozród',
  'rozr-d': 'Rozród',
  'og-lny': 'Pozostałe',
  specjalistyka: 'Specjalistyka',
};

interface InlineService {
  slug: string;
  name_pl?: string;
  category?: string;
}
interface Procedure {
  service_slug: string;
  offered?: boolean;
}

function defaultRail(): NavOverrideRail {
  return { show_about: true, show_reviews: true, show_contact: true, custom: [] };
}

function autoDerive(
  procedures: Procedure[],
  inlineByslug: Map<string, InlineService>,
): NavOverrideColumn[] {
  const colMap = new Map<string, NavOverrideItem[]>();
  for (const p of procedures) {
    if (p.offered === false) continue;
    const inline = inlineByslug.get(p.service_slug);
    const catRaw = inline?.category ?? '';
    const colLabel = VET_CATEGORY_LABEL_PL[catRaw] ?? 'Pozostałe';
    const list = colMap.get(colLabel) ?? [];
    list.push({
      label: inline?.name_pl ?? p.service_slug,
      service_slug: p.service_slug,
    });
    colMap.set(colLabel, list);
  }
  return Array.from(colMap.entries())
    .slice(0, 4)
    .map(([label, items]) => ({ label, items: items.slice(0, 10) }));
}

function MenuPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const inlineServices = ((brief as any)?.inline_services ?? []) as InlineService[];
  const procedures = ((brief as any)?.procedures ?? []) as Procedure[];
  const rawOverride = (brief as any)?.nav_override as NavOverride | undefined;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const inlineByslug = React.useMemo(
    () => new Map(inlineServices.map((s) => [s.slug, s])),
    [inlineServices],
  );

  const [columns, setColumns] = React.useState<NavOverrideColumn[]>(
    () => rawOverride?.columns ?? [],
  );
  const [rail, setRail] = React.useState<NavOverrideRail>(
    () => rawOverride?.right_rail ?? defaultRail(),
  );
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  // Sync from server on first load (brief is async).
  React.useEffect(() => {
    if (dirty) return;
    setColumns(rawOverride?.columns ?? []);
    setRail(rawOverride?.right_rail ?? defaultRail());
  }, [rawOverride, dirty]);

  const markDirty = () => {
    setDirty(true);
    setSavedStatus('idle');
  };

  const allAssignedSlugs = new Set(columns.flatMap((c) => c.items.map((i) => i.service_slug)));
  const availableProcedures = procedures
    .filter((p) => p.offered !== false)
    .filter((p) => !allAssignedSlugs.has(p.service_slug));

  const handleAutoFill = () => {
    const derived = autoDerive(procedures, inlineByslug);
    setColumns(derived);
    markDirty();
  };

  const addColumn = () => {
    if (columns.length >= 4) return;
    setColumns((prev) => [...prev, { label: 'Nowa kategoria', items: [] }]);
    markDirty();
  };

  const removeColumn = (colIdx: number) => {
    setColumns((prev) => prev.filter((_, i) => i !== colIdx));
    markDirty();
  };

  const moveColumn = (colIdx: number, direction: -1 | 1) => {
    const newIdx = colIdx + direction;
    if (newIdx < 0 || newIdx >= columns.length) return;
    setColumns((prev) => {
      const next = [...prev];
      [next[colIdx]!, next[newIdx]!] = [next[newIdx]!, next[colIdx]!];
      return next;
    });
    markDirty();
  };

  const updateColumnLabel = (colIdx: number, label: string) => {
    setColumns((prev) =>
      prev.map((c, i) => (i === colIdx ? { ...c, label } : c)),
    );
    markDirty();
  };

  const addItem = (colIdx: number, service_slug: string) => {
    const inline = inlineByslug.get(service_slug);
    const label = inline?.name_pl ?? service_slug;
    setColumns((prev) =>
      prev.map((c, i) =>
        i === colIdx ? { ...c, items: [...c.items, { label, service_slug }] } : c,
      ),
    );
    markDirty();
  };

  const removeItem = (colIdx: number, itemIdx: number) => {
    setColumns((prev) =>
      prev.map((c, i) =>
        i === colIdx ? { ...c, items: c.items.filter((_, j) => j !== itemIdx) } : c,
      ),
    );
    markDirty();
  };

  const moveItem = (colIdx: number, itemIdx: number, direction: -1 | 1) => {
    const newIdx = itemIdx + direction;
    setColumns((prev) =>
      prev.map((c, i) => {
        if (i !== colIdx) return c;
        if (newIdx < 0 || newIdx >= c.items.length) return c;
        const items = [...c.items];
        [items[itemIdx]!, items[newIdx]!] = [items[newIdx]!, items[itemIdx]!];
        return { ...c, items };
      }),
    );
    markDirty();
  };

  const updateItemLabel = (colIdx: number, itemIdx: number, label: string) => {
    setColumns((prev) =>
      prev.map((c, i) =>
        i === colIdx
          ? {
              ...c,
              items: c.items.map((it, j) => (j === itemIdx ? { ...it, label } : it)),
            }
          : c,
      ),
    );
    markDirty();
  };

  const updateRail = (patch: Partial<NavOverrideRail>) => {
    setRail((prev) => ({ ...prev, ...patch }));
    markDirty();
  };

  const addCustomRail = () => {
    if (rail.custom.length >= 4) return;
    updateRail({ custom: [...rail.custom, { label: '', href: '' }] });
  };

  const updateCustomRail = (idx: number, patch: Partial<{ label: string; href: string }>) => {
    updateRail({
      custom: rail.custom.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });
  };

  const removeCustomRail = (idx: number) => {
    updateRail({ custom: rail.custom.filter((_, i) => i !== idx) });
  };

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      // Drop empty columns/items so the server schema validates cleanly
      // (NavOverride requires columns.items.min(1)).
      const cleanedColumns = columns
        .map((c) => ({
          label: c.label.trim(),
          items: c.items
            .filter((i) => i.label.trim().length > 0)
            .map((i) => ({ label: i.label.trim(), service_slug: i.service_slug })),
        }))
        .filter((c) => c.label.length > 0 && c.items.length > 0);
      const cleanedRail: NavOverrideRail = {
        ...rail,
        custom: rail.custom
          .map((c) => ({ label: c.label.trim(), href: c.href.trim() }))
          .filter((c) => c.label.length > 0 && c.href.length > 0),
      };
      // If columns are empty, save null override (= auto-derive).
      const data =
        cleanedColumns.length === 0
          ? null
          : { columns: cleanedColumns, right_rail: cleanedRail };
      await update.mutateAsync({ slice: 'nav_override', data });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const hasOverride = columns.length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Menu nawigacji
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Skonfiguruj megamenu „Zabiegi" — pogrupuj zabiegi w kolumny i wybierz, co
          pokazać w prawej kolumnie szybkich linków. Pozostaw pustym, by menu
          generowało się automatycznie z listy zabiegów.
        </p>
      </header>

      {!hasOverride ? (
        <section className="rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
          <h2 className="font-display text-lg font-semibold text-text">
            Automatyczne menu (domyślnie)
          </h2>
          <p className="mt-2 text-sm text-text-muted">
            Menu pokazuje wszystkie oferowane zabiegi, pogrupowane po kategorii
            (Chirurgia, Diagnostyka, Stomatologia, …). Możesz to zostawić jak
            jest — to działa bez konfiguracji.
          </p>
          <p className="mt-2 text-sm text-text-muted">
            Jeśli wolisz mieć kontrolę nad tym, co pokazujemy w menu i w jakiej
            kolejności — wczytaj propozycję poniżej i edytuj ręcznie.
          </p>
          <div className="mt-4 flex gap-3">
            <Button type="button" variant="secondary" onClick={handleAutoFill}>
              <Wand2 className="size-4" aria-hidden="true" /> Wczytaj propozycję
            </Button>
            <Button type="button" variant="ghost" onClick={addColumn}>
              <Plus className="size-4" aria-hidden="true" /> Zacznij od pustej kolumny
            </Button>
          </div>
          {procedures.filter((p) => p.offered !== false).length === 0 ? (
            <p className="mt-3 text-xs text-warning">
              Najpierw dodaj zabiegi w sekcji „Usługi" — bez nich megamenu nie ma co
              pokazać.
            </p>
          ) : null}
        </section>
      ) : null}

      {hasOverride ? (
        <>
          {/* Columns */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-text">
                Kolumny megamenu
              </h2>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={handleAutoFill}>
                  <Wand2 className="size-4" aria-hidden="true" /> Przeładuj automatycznie
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={addColumn}
                  disabled={columns.length >= 4}
                >
                  <Plus className="size-4" aria-hidden="true" /> Dodaj kolumnę
                </Button>
              </div>
            </div>

            <ul role="list" className="space-y-4">
              {columns.map((col, colIdx) => (
                <li
                  key={colIdx}
                  className="rounded-[var(--radius-card)] bg-bg p-5 ring-1 ring-black/5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        aria-label="Przesuń w lewo"
                        disabled={colIdx === 0}
                        onClick={() => moveColumn(colIdx, -1)}
                        className="rounded p-1 text-text-subtle disabled:opacity-30 hover:text-text"
                      >
                        <ArrowUp className="size-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label="Przesuń w prawo"
                        disabled={colIdx === columns.length - 1}
                        onClick={() => moveColumn(colIdx, 1)}
                        className="rounded p-1 text-text-subtle disabled:opacity-30 hover:text-text"
                      >
                        <ArrowDown className="size-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="flex-1">
                      <Field label="Nazwa kolumny" htmlFor={`col-${colIdx}`}>
                        <Input
                          id={`col-${colIdx}`}
                          value={col.label}
                          maxLength={40}
                          onChange={(e) => updateColumnLabel(colIdx, e.target.value)}
                        />
                      </Field>
                    </div>
                    <button
                      type="button"
                      aria-label="Usuń kolumnę"
                      onClick={() => removeColumn(colIdx)}
                      className="mt-7 rounded p-1 text-text-subtle hover:text-error"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </button>
                  </div>

                  <ul role="list" className="mt-4 space-y-1.5">
                    {col.items.map((item, itemIdx) => (
                      <li
                        key={`${item.service_slug}-${itemIdx}`}
                        className="flex items-center gap-2 rounded-md bg-surface px-3 py-2"
                      >
                        <div className="flex flex-col">
                          <button
                            type="button"
                            aria-label="Przesuń w górę"
                            disabled={itemIdx === 0}
                            onClick={() => moveItem(colIdx, itemIdx, -1)}
                            className="rounded p-0.5 text-text-subtle disabled:opacity-30 hover:text-text"
                          >
                            <ArrowUp className="size-3" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            aria-label="Przesuń w dół"
                            disabled={itemIdx === col.items.length - 1}
                            onClick={() => moveItem(colIdx, itemIdx, 1)}
                            className="rounded p-0.5 text-text-subtle disabled:opacity-30 hover:text-text"
                          >
                            <ArrowDown className="size-3" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <Input
                            value={item.label}
                            maxLength={60}
                            onChange={(e) =>
                              updateItemLabel(colIdx, itemIdx, e.target.value)
                            }
                            placeholder="Nazwa zabiegu"
                          />
                          <p className="mt-0.5 font-mono text-[10px] text-text-subtle">
                            /zabiegi/{item.service_slug}
                          </p>
                        </div>
                        <button
                          type="button"
                          aria-label="Usuń zabieg z kolumny"
                          onClick={() => removeItem(colIdx, itemIdx)}
                          className="rounded p-1 text-text-subtle hover:text-error"
                        >
                          <X className="size-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>

                  <AddItemPicker
                    available={availableProcedures}
                    inlineByslug={inlineByslug}
                    onPick={(slug) => addItem(colIdx, slug)}
                    disabled={col.items.length >= 10}
                  />
                </li>
              ))}
            </ul>
          </section>

          {/* Right rail */}
          <section className="rounded-[var(--radius-card)] bg-bg p-5 ring-1 ring-black/5">
            <h2 className="font-display text-lg font-semibold text-text">
              Prawa kolumna (szybkie linki)
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Linki, które pojawią się obok kolumn z zabiegami. Pomagają pacjentom
              przeskoczyć do najczęściej szukanych miejsc na stronie.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  checked={rail.show_about}
                  onCheckedChange={(v) => updateRail({ show_about: v })}
                />
                <span>O klinice</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  checked={rail.show_reviews}
                  onCheckedChange={(v) => updateRail({ show_reviews: v })}
                />
                <span>Opinie</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  checked={rail.show_contact}
                  onCheckedChange={(v) => updateRail({ show_contact: v })}
                />
                <span>Kontakt</span>
              </label>
            </div>

            {rail.custom.length > 0 || rail.custom.length < 4 ? (
              <div className="mt-5 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">
                  Dodatkowe linki ({rail.custom.length} / 4)
                </p>
                {rail.custom.map((c, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={c.label}
                      maxLength={40}
                      onChange={(e) => updateCustomRail(idx, { label: e.target.value })}
                      placeholder="Etykieta (np. Cennik)"
                      className="flex-1"
                    />
                    <Input
                      value={c.href}
                      onChange={(e) => updateCustomRail(idx, { href: e.target.value })}
                      placeholder="/cennik lub https://..."
                      className="flex-1 font-mono text-xs"
                    />
                    <button
                      type="button"
                      aria-label="Usuń link"
                      onClick={() => removeCustomRail(idx)}
                      className="rounded p-2 text-text-subtle hover:text-error"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
                {rail.custom.length < 4 ? (
                  <Button type="button" variant="ghost" size="sm" onClick={addCustomRail}>
                    <Plus className="size-3.5" aria-hidden="true" /> Dodaj link
                  </Button>
                ) : null}
              </div>
            ) : null}
          </section>

          <p className="text-xs text-text-subtle">
            Wyczyść wszystkie kolumny i zapisz, by przywrócić automatyczne menu.
          </p>
        </>
      ) : null}

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        onCancel={() => {
          setColumns(rawOverride?.columns ?? []);
          setRail(rawOverride?.right_rail ?? defaultRail());
          setDirty(false);
        }}
      />
    </form>
  );
}

/**
 * Inline picker that opens a tiny popover with available procedures.
 * Doesn't use the shadcn DropdownMenu primitive to keep the surface
 * predictable inside a scrollable form. Uses native <details>.
 */
function AddItemPicker({
  available,
  inlineByslug,
  onPick,
  disabled,
}: {
  available: Procedure[];
  inlineByslug: Map<string, InlineService>;
  onPick: (slug: string) => void;
  disabled: boolean;
}) {
  const detailsRef = React.useRef<HTMLDetailsElement | null>(null);
  if (available.length === 0) {
    return (
      <p className="mt-3 text-xs text-text-subtle">
        {disabled
          ? 'Limit 10 zabiegów na kolumnę.'
          : 'Wszystkie oferowane zabiegi są już przypisane do kolumn.'}
      </p>
    );
  }
  return (
    <details
      ref={detailsRef}
      className={cn(
        'mt-3 inline-block',
        disabled ? 'opacity-50 pointer-events-none' : '',
      )}
    >
      <summary className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-surface px-3 py-1.5 text-sm text-text hover:bg-bg/60">
        <Plus className="size-3.5" aria-hidden="true" /> Dodaj zabieg{' '}
        <ChevronDown className="size-3 text-text-subtle" aria-hidden="true" />
      </summary>
      <div className="mt-2 max-h-64 w-72 overflow-y-auto rounded-md border border-border bg-bg p-1 shadow-lg ring-1 ring-black/5">
        <ul role="list">
          {available.map((p) => {
            const inline = inlineByslug.get(p.service_slug);
            const name = inline?.name_pl ?? p.service_slug;
            return (
              <li key={p.service_slug}>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-1.5 text-left text-sm text-text hover:bg-surface"
                  onClick={() => {
                    onPick(p.service_slug);
                    detailsRef.current?.removeAttribute('open');
                  }}
                >
                  <span>{name}</span>
                  <span className="ml-2 font-mono text-[10px] text-text-subtle">
                    {p.service_slug}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}
